import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ShopHeader from "@/components/app/ShopHeader";
import SareeCard from "@/components/app/SareeCard";
import FilterSheet from "@/components/app/FilterSheet";
import { Search, Sparkles } from "lucide-react";

const STATIC_QUICK = [
    { key: "all", label: "All", filters: {} },
    { key: "trending", label: "Trending", filters: { trending: true } },
    { key: "new", label: "New arrivals", filters: { new_arrivals: true } },
];

export default function Catalog() {
    const { slug } = useParams();
    const [params, setParams] = useSearchParams();
    const [shop, setShop] = useState(null);
    const [categories, setCategories] = useState([]);
    const [sarees, setSarees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quick, setQuick] = useState(params.get("quick") || "all");
    const [q, setQ] = useState(params.get("q") || "");
    const [sort, setSort] = useState("new");

    const [filters, setFilters] = useState({
        occasion: "", fabric: "", brand: "", color: "",
        min_price: 0, max_price: 0,
        trending: false, new_arrivals: false,
    });

    useEffect(() => {
        Promise.all([
            api.get(`/shops/${slug}`),
            api.get(`/shops/${slug}/categories`),
        ]).then(([{ data: s }, { data: c }]) => {
            setShop(s);
            setCategories(c);
            setFilters((f) => ({ ...f, min_price: s.facets?.min_price || 0, max_price: s.facets?.max_price || 0 }));
        }).catch((e) => {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Shop not found");
        });
    }, [slug]);

    const QUICK = useMemo(() => {
        const dyn = categories.map((c) => ({ key: c.slug, label: c.name, filters: { category: c.slug } }));
        return [...STATIC_QUICK, ...dyn];
    }, [categories]);

    const activeFilters = useMemo(() => {
        const quickF = QUICK.find((x) => x.key === quick)?.filters || {};
        return { ...filters, ...quickF, q, sort };
    }, [filters, quick, q, sort, QUICK]);

    useEffect(() => {
        if (!shop) return;
        setLoading(true);
        const p = {};
        Object.entries(activeFilters).forEach(([k, v]) => {
            if (v === "" || v === false || v === null || v === undefined) return;
            if (k === "min_price" && v === (shop.facets?.min_price || 0)) return;
            if (k === "max_price" && v === (shop.facets?.max_price || 0)) return;
            p[k] = v;
        });
        api.get(`/shops/${slug}/sarees`, { params: p }).then(({ data }) => {
            setSarees(data);
        }).catch((e) => {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        }).finally(() => setLoading(false));
    }, [shop, slug, activeFilters]);

    const onQuick = (k) => {
        setQuick(k);
        const np = new URLSearchParams(params);
        if (k === "all") np.delete("quick"); else np.set("quick", k);
        setParams(np);
    };

    return (
        <div className="min-h-screen bg-brand-beige pb-20">
            <ShopHeader shop={shop} slug={slug} />

            {/* Editorial header */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16">
                <div className="ayraa-overline mb-5">
                    <span className="inline-block w-6 h-px bg-brand-bronze align-middle mr-2.5" />
                    The Collection
                </div>
                <h1 className="editorial-h2 text-balance">
                    {shop?.name || "—"}
                </h1>
                <p className="mt-4 text-brand-muted text-sm sm:text-base max-w-xl text-pretty">
                    {shop?.location ? <>{shop.location} · </> : null}
                    <span className="font-mono">{shop?.saree_count ?? "—"}</span> pieces curated for the season.
                </p>
                <div className="mt-10 h-px bg-brand-border" />
            </section>

            {/* Search + filters */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-10">
                <div className="relative max-w-xl">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-brand-soft" strokeWidth={1.5} />
                    <input
                        className="ayraa-input pl-11"
                        placeholder="Search by title, brand, fabric…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="catalog-search-input"
                    />
                </div>

                {/* quick chips */}
                <div className="mt-6 -mx-5 px-5 sm:mx-0 sm:px-0 overflow-x-auto scroll-x">
                    <div className="flex items-center gap-1.5 min-w-max">
                        {QUICK.map((qk) => (
                            <button
                                key={qk.key}
                                onClick={() => onQuick(qk.key)}
                                className={`text-xs font-mono uppercase tracking-wider-2 px-4 py-2 border transition-all duration-300 whitespace-nowrap rounded-sm ${
                                    quick === qk.key
                                        ? "bg-brand-ink text-white border-brand-ink"
                                        : "bg-transparent border-brand-border text-brand-ink hover:border-brand-ink"
                                }`}
                                data-testid={`quick-${qk.key}`}
                            >{qk.label}</button>
                        ))}
                    </div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                    {shop && <FilterSheet facets={shop.facets} value={filters} onApply={setFilters} />}
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="ayraa-input max-w-[200px] py-2 text-xs font-mono uppercase tracking-wider-2"
                        data-testid="sort-select"
                    >
                        <option value="new">Newest first</option>
                        <option value="price_asc">Price: low → high</option>
                        <option value="price_desc">Price: high → low</option>
                        <option value="trending">Trending first</option>
                    </select>
                </div>
            </section>

            {/* Editorial grid */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-12 sm:mt-16">
                {loading ? (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12" data-testid="catalog-loading">
                        {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-sm skel" />)}
                    </div>
                ) : sarees.length === 0 ? (
                    <div className="text-center py-24 border border-brand-border bg-brand-paper">
                        <Sparkles className="w-7 h-7 text-brand-bronze mx-auto" strokeWidth={1.2} />
                        <div className="font-display text-2xl mt-6 tracking-editorial">The collection is being curated</div>
                        <p className="text-brand-muted mt-3 max-w-md mx-auto text-sm">Try resetting or removing a filter to see more pieces.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 animate-fade-up" data-testid="catalog-grid">
                        {sarees.map((s) => <SareeCard key={s.id} saree={s} shopSlug={slug} />)}
                    </div>
                )}
            </section>
        </div>
    );
}
